import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { ErrorBoundary } from 'react-error-boundary';

import {
  config,
  selectors,
  getFlagsSelector,
  actions as coreActions,
  apiImpersonate,
  Sentry,
  TOKEN_PROVIDERS,
} from '@formue-app/core';

import { useRoutes } from './config/routes';
import { usePageTracking } from './services/tracking/usePageTracking';
import { useAnalyticsPageTracking } from './services/tracking/useAnalyticsPageTracking';
import { useQueryState } from './services/hooks/queryState';

import { PrintDefault } from './components/print/PrintDefault';
import { ErrorFallback } from './components/common/ErrorFallback';
import { LeadRegistrationModal } from './components/lead/LeadRegistrationModal';
import { PodcastPopover } from './components/popover/PodcastPopover';

import { GlobalFonts } from './fonts';
import { ResetStyles } from './reset';

import { actions as appActions } from './store/app';
import { actions as authActions } from './store/auth';
import { actions as webAuthnActions } from './store/webauthn';
// Imported directly (not via the pages barrel) so the eager app-shell fallback
// doesn't pull every route page into the initial bundle — see lazyPages.js.
import { MenuPage } from './pages/base/MenuPage';
import { CenteredActivityIndicator } from './components/common/ActivityIndicator';

const AIChatWidget = React.lazy(() =>
  import('./components/ai/AIChatWidget').then((module) => ({
    default: module.AIChatWidget,
  }))
);

const { currentUserSelector } = selectors.ui.user;
const { user: userActions } = coreActions.ui;

const App = () => {
  document.title = config.title;
  const routes = useRoutes();
  const location = useLocation();
  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState(false);
  usePageTracking();
  useAnalyticsPageTracking();
  const { pathname, search } = location;
  const [exportMode] = useQueryState('exportMode', false);
  const user = useSelector(currentUserSelector);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  /**
   * If there is a `access_token` hash param in the URL, we wan't to use the value of that as the
   * token and store that as we would have if the user authenticated through one of the regular
   * login routes
   */
  const hashParams = new URLSearchParams(document.location.hash.substring(1));
  const accessToken = hashParams.get('access_token');

  /**
   * The PDF renderer of an /oidc session gets a single-use `login_assertion` instead, which
   * we exchange for tokens bound to a DPoP key of its own. Read on the first render only:
   * a spent assertion must never be exchanged again.
   */
  const [loginAssertion] = useState(() => hashParams.get('login_assertion'));

  // Load webauthn stuff on app init
  dispatch(webAuthnActions.load());

  if (accessToken) {
    dispatch(authActions.setAccessToken(accessToken));
    dispatch(authActions.finishAuthentication(`${pathname}${search}`));
  }

  useEffect(() => {
    if (!loginAssertion) return;

    // Drop the assertion from the URL, keeping the query (impersonationId, language, slides)
    // that finishAuthentication navigates back to.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(window.history.state, '', returnTo);
    dispatch(
      authActions.startAuthentication({
        exchange: 'loginAssertion',
        credentials: { assertion: loginAssertion },
        pathname: returnTo,
        provider: TOKEN_PROVIDERS.OIDC,
      })
    );
  }, [dispatch, loginAssertion]);

  /**
   * If we pass in a language query param we want to override any stored preferences for
   * language and use the given language instead
   */
  const queryParams = new URLSearchParams(
    document.location.search.substring(1)
  );

  // Hack to make text update to correct language
  // this triggers a re-render when the language changes
  let language = useSelector((state) => state.ui.user.language);
  language = queryParams.get('language');
  if (language) {
    dispatch(userActions.setLanguage(language));
  }

  /**
   * If we pass in an impersonationId query param we store that in sessionStorage
   * so that after we are authenticated we will automatically be impersonating
   * this user without the need for the advisor to select the client from the list
   *
   * This is used in a few instances where we automatically log users in, either
   * via the Azure SSO solution, or using the #access_token=foobar trick.
   */
  const impersonationId = queryParams.get('impersonationId');
  if (impersonationId) {
    window.sessionStorage.setItem('impersonationId', impersonationId);
    apiImpersonate(impersonationId);
  }

  /**
   * Configure the auth timeout, for regular users we want to use the configured
   * value, but for employees we check the expiration of the token and use that
   * as the idle timeout. Ensuring that employees stayes logged in for as long
   * as the token is valid.
   */
  const { exp, isEmployee } = useSelector((state) => state.auth);
  let authTimeout = config.auth.timeout;
  if (isEmployee && exp) {
    // find number of ms until expiration of token and use that as auth timeout
    const now = Date.now();
    const msLeftUntilTokenExpiration = exp * 1000 - now;
    // A token that's already expired would produce a negative/zero timeout,
    // which react-idle-timer rejects (it must exceed promptBeforeIdle) and
    // crashes the app. Clamp to a small floor so we still log out promptly.
    authTimeout = Math.max(msLeftUntilTokenExpiration, 1000);
  }

  const { isLead, features } = useSelector(getFlagsSelector);
  const [registrationModalIsOpen, setRegistrationModalIsOpen] = useState(false);

  const handleOnIdle = () => {
    dispatch(
      authActions.logout({
        state: {
          error: 'SessionTimeout',
        },
      })
    );
  };

  useIdleTimer({
    timeout: authTimeout,
    onIdle: handleOnIdle,
  });

  /**
   * Determine if we should open the lead registration modal
   */
  useEffect(() => {
    if (!user || !isLead) return;
    const { email } = user;

    if (!email) {
      setRegistrationModalIsOpen(true);
    }
  }, [user, isLead]);

  // In the case where we are in the middle of the authentication flow
  // we don't want to re-initialize the app, that would disrupt the state
  // and cause problems with the re-authentication flow.
  const isPublicPath = routes.some((r) => r.public && r.path === pathname);

  // A login assertion is still being exchanged: init now would find no token and send the
  // renderer to /login. finishAuthentication inits the app once the exchange lands.
  useEffect(() => {
    if (!initialized && !isPublicPath && !loginAssertion) {
      dispatch(appActions.init());
      setInitialized(true);
    }
  }, [dispatch, initialized, isPublicPath, loginAssertion]);

  return (
    <>
      <ResetStyles />
      <GlobalFonts />
      <PrintDefault>
        {/* Data-router scroll handling: scroll to top on new navigations,
            restore the saved position on back/forward. Keying by pathname also
            restores scroll when returning to a page (e.g. holdings ← securities),
            which the old ScrollToTop exclusion list handled by hand. */}
        <ScrollRestoration getKey={(location) => location.pathname} />
        {/* Catches a failed page chunk (lazy load on a poor connection) and
            shows the error screen instead of a blank page; resets when the user
            navigates elsewhere. */}
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error) => Sentry.captureException(error)}
          resetKeys={[pathname]}
        >
          {/* The app shell is only a sensible placeholder for pages that live
              inside it; on public pages (login, auth callback) it would flash
              the menu and kick off authenticated requests. */}
          <Suspense
            fallback={
              isPublicPath ? (
                <div style={{ height: '100vh' }}>
                  <CenteredActivityIndicator />
                </div>
              ) : (
                <MenuPage />
              )
            }
          >
            <Outlet />
            {isAuthenticated ? <PodcastPopover /> : null}
            <LeadRegistrationModal
              isOpen={registrationModalIsOpen}
              onRequestClose={() => setRegistrationModalIsOpen(false)}
            />
          </Suspense>
        </ErrorBoundary>
        {/* The chat widget is ancillary, so it gets its own boundary: a failed
            widget chunk degrades to no widget rather than replacing the page.
            `exportMode` pages are rendered headlessly into a PDF. */}
        {isAuthenticated && features?.aiAgent && !exportMode ? (
          <ErrorBoundary
            fallbackRender={() => null}
            onError={(error) => Sentry.captureException(error)}
            resetKeys={[pathname]}
          >
            <Suspense fallback={null}>
              <AIChatWidget />
            </Suspense>
          </ErrorBoundary>
        ) : null}
      </PrintDefault>
    </>
  );
};

export default App;
